/**
 * Los efectos de la partida (§0.6).
 *
 * **Se sintetizan, no se descargan.** Ni un archivo de audio: el aula puede no tener red hacia
 * fuera, el proyecto no depende de CDN alguno y seis efectos de medio segundo pesarían más que todo
 * el código que los genera. Son osciladores y envolventes, nada más.
 *
 * **Suenan solo en la pantalla de quien aloja la partida.** Cuarenta móviles pitando a destiempo es
 * ruido, no ambiente: es lo primero que hace que una clase pida apagarlo todo.
 *
 * **Y nada depende de que se oigan** (principio IV). Cada efecto acompaña a algo que ya está en
 * pantalla; con el sonido apagado no se pierde ninguna información.
 */

export type SoundEvent =
  'countdown' | 'tick' | 'questionClosed' | 'revealUp' | 'revealDown' | 'leaderboard' | 'podium';

const STORAGE_KEY = 'clica.sound';

/** Si el sonido está activado. Se recuerda en el propio dispositivo, no en el perfil. */
export function readSoundEnabled(storage: Storage = window.localStorage): boolean {
  try {
    // Por defecto **apagado**: un aula en la que la herramienta empieza a sonar sola sin avisar es
    // una herramienta que se desactiva entera.
    return storage.getItem(STORAGE_KEY) === 'on';
  } catch {
    return false;
  }
}

export function writeSoundEnabled(enabled: boolean, storage: Storage = window.localStorage): void {
  try {
    storage.setItem(STORAGE_KEY, enabled ? 'on' : 'off');
  } catch {
    // Modo privado o almacenamiento bloqueado. El sonido funciona igual en esta sesión.
  }
}

/** Una nota: frecuencia en hercios, cuándo empieza y cuánto dura, en segundos desde ahora. */
interface Note {
  readonly hz: number;
  readonly at: number;
  readonly seconds: number;
  readonly gain?: number;
  readonly type?: OscillatorType;
}

/**
 * Las partituras.
 *
 * Cortas a propósito: §0.5 pide tono «directo y de aula, no infantil». Un efecto de dos segundos
 * en cada pregunta acaba siendo insufrible a la décima.
 */
const SCORES: Readonly<Record<SoundEvent, readonly Note[]>> = {
  // Tres pulsos ascendentes.
  countdown: [
    { hz: 440, at: 0, seconds: 0.12 },
    { hz: 554, at: 0.35, seconds: 0.12 },
    { hz: 659, at: 0.7, seconds: 0.18 },
  ],
  // Tic-tac discreto de los últimos segundos.
  tick: [{ hz: 1200, at: 0, seconds: 0.03, gain: 0.08, type: 'square' }],
  // Golpe seco al cerrar.
  questionClosed: [{ hz: 160, at: 0, seconds: 0.14, gain: 0.25 }],
  // Acorde ascendente: la mayoría acertó.
  revealUp: [
    { hz: 523, at: 0, seconds: 0.18 },
    { hz: 659, at: 0.09, seconds: 0.18 },
    { hz: 784, at: 0.18, seconds: 0.3 },
  ],
  // Descendente: la mayoría falló. Sin dramatismo: es información, no un castigo.
  revealDown: [
    { hz: 494, at: 0, seconds: 0.18 },
    { hz: 415, at: 0.09, seconds: 0.18 },
    { hz: 330, at: 0.18, seconds: 0.3 },
  ],
  // Barrido corto al entrar el ranking.
  leaderboard: [
    { hz: 330, at: 0, seconds: 0.08 },
    { hz: 494, at: 0.06, seconds: 0.08 },
    { hz: 659, at: 0.12, seconds: 0.14 },
  ],
  // Fanfarria de dos segundos, la única que se permite durar.
  podium: [
    { hz: 523, at: 0, seconds: 0.2 },
    { hz: 659, at: 0.18, seconds: 0.2 },
    { hz: 784, at: 0.36, seconds: 0.25 },
    { hz: 1047, at: 0.6, seconds: 0.6 },
    { hz: 784, at: 0.6, seconds: 0.6, gain: 0.12 },
  ],
};

/**
 * El reproductor.
 *
 * El contexto de audio se crea **en el primer gesto**, no al cargar: los navegadores lo suspenden si
 * se crea sin interacción, y el efecto de la cuenta atrás —el primero que suena— se perdería.
 */
export class SoundBoard {
  private context: AudioContext | null = null;

  constructor(private enabled: boolean) {}

  setEnabled(value: boolean): void {
    this.enabled = value;
    if (!value) return;
    // Se despierta aquí porque activar el sonido ES el gesto que el navegador exige.
    void this.context?.resume();
  }

  play(event: SoundEvent): void {
    if (!this.enabled) return;

    const context = this.ensureContext();
    if (context === null) return;

    const now = context.currentTime;

    for (const note of SCORES[event]) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();

      oscillator.type = note.type ?? 'sine';
      oscillator.frequency.value = note.hz;

      // La envolvente evita el chasquido que produce cortar una onda en seco, que en un altavoz de
      // proyector suena peor que el propio efecto.
      const peak = note.gain ?? 0.18;
      const start = now + note.at;
      const end = start + note.seconds;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(peak, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, end);

      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start);
      oscillator.stop(end + 0.02);
    }
  }

  close(): void {
    void this.context?.close();
    this.context = null;
  }

  private ensureContext(): AudioContext | null {
    if (this.context !== null) return this.context;

    try {
      this.context = new AudioContext();
      return this.context;
    } catch {
      // Sin audio disponible se sigue jugando: nada depende de que suene.
      return null;
    }
  }
}

/**
 * Vibración corta al confirmar la respuesta, **lo único que el móvil del alumno hace** (§0.6).
 *
 * Se comprueba que exista porque no todos los navegadores la tienen, y su ausencia no cambia nada.
 */
export function buzz(ms = 30): void {
  if (typeof navigator === 'undefined') return;
  if (typeof navigator.vibrate !== 'function') return;
  navigator.vibrate(ms);
}
